/**
 * Turning failures into something a shopkeeper can act on.
 *
 * The rule this file exists to enforce: a person standing at a till, with a
 * customer waiting, should never be shown a sentence written for a backend
 * developer. They cannot act on a class name. They need to know whether to wait,
 * retry, call someone, or carry on selling.
 *
 * The specific failure that prompted it: NestJS's throttler answers a 429 with
 * `{ "message": "ThrottlerException: Too Many Requests" }`, and our error
 * handling preferred the server's message over our own copy — so that string
 * went verbatim onto the login screen and into the sync status tooltip.
 */

import { HttpError } from '@/services/httpClient';

/**
 * Does this read like it was written for a developer rather than a shopkeeper?
 *
 * Server messages are usually worth showing — a validation error like "Phone
 * number is not registered" is far better copy than anything generic we could
 * write. Framework exception strings are not. They are recognisable: a
 * PascalCase type name, then a colon.
 */
export function isTechnicalMessage(msg: string): boolean {
  const s = msg.trim();
  if (!s) return true;
  // "ThrottlerException: Too Many Requests", "TypeError: fetch failed",
  // "PrismaClientKnownRequestError: ..." — a type name doing the talking.
  if (/^[A-Z][A-Za-z0-9_]*(Exception|Error)\b/.test(s)) return true;
  // Bare "HTTP 500" / "Request failed with status code 503".
  if (/^HTTP\s+\d{3}\b/i.test(s)) return true;
  // Anything carrying a stack frame or a source path.
  if (/\bat\s+\w+\s+\(/.test(s) || /\.(ts|tsx|js|jsx):\d+/.test(s)) return true;
  // SQL and Prisma codes leaking through. Postgres SQLSTATEs are five
  // characters and often all digits ("23505 duplicate key value"), so the
  // leading letter cannot be required.
  if (/^[A-Z0-9]\d{4}\b/.test(s) || /\bP\d{4}\b/.test(s)) return true;
  // Node/libuv errno codes — ENOTFOUND, ECONNREFUSED, ETIMEDOUT. These reach
  // the UI whenever DNS or the socket fails rather than the request itself.
  if (/^E[A-Z]{3,}\b/.test(s)) return true;
  return false;
}

/** Our own copy for the status codes that actually reach a till. */
function byStatus(status: number): string | null {
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'Your account does not have permission for this.';
  if (status === 404) return 'The server could not find what was asked for.';
  if (status === 409) return 'This was changed somewhere else. Refresh and try again.';
  if (status === 413) return 'There is too much to send at once. It will go through in smaller batches.';
  if (status === 429) return 'The server is busy. This will retry on its own in a minute.';
  if (status === 503) return 'The server is temporarily unavailable. Selling still works offline.';
  if (status >= 500) return 'The server had a problem. Selling still works offline.';
  return null;
}

/**
 * The general-purpose translator.
 *
 * Prefers a human server message, falls back to our own copy per status, and
 * never lets a framework string through. `fallback` is the caller's last resort
 * so each surface can say something appropriate to itself.
 */
export function describeError(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof HttpError) {
    const body = err.body as { message?: string | string[] } | null;
    const raw = Array.isArray(body?.message) ? body?.message[0] : body?.message;
    if (typeof raw === 'string' && raw.trim() && !isTechnicalMessage(raw)) return raw;
    return byStatus(err.status) ?? fallback;
  }

  // fetch() rejects with a TypeError when the network or backend is unreachable.
  if (err instanceof TypeError) return 'Cannot reach the server. Check the connection.';

  const msg = (err as Error)?.message;
  if (typeof msg === 'string' && msg.trim() && !isTechnicalMessage(msg)) return msg;
  return fallback;
}

/**
 * Sync's own wording.
 *
 * Sync fails in the background while the till keeps working, so every message
 * here has to answer the only question that matters to the person holding the
 * scanner: can I keep selling? The answer is almost always yes, and saying so
 * is more useful than naming the cause.
 */
export function describeSyncError(err: unknown): string {
  const status = err instanceof HttpError ? err.status : null;
  const raw = (err as Error)?.message ?? '';

  // Rate limiting does not always arrive as an HttpError — it can come back
  // through WatermelonDB's synchronize() wrapped as a plain Error carrying the
  // text. Match on either.
  if (status === 429 || /\b429\b|too many requests|throttler/i.test(raw)) {
    return 'The server is busy. Your sales are saved here and will upload shortly.';
  }
  if (status === 401) return 'Your session has expired. Sign in again to upload your sales.';
  if (status === 403) return 'This device is not allowed to sync. Contact the owner.';
  if (err instanceof TypeError || /network|fetch|offline|failed to fetch/i.test(raw)) {
    return 'No connection. Your sales are saved here and will upload when you are back online.';
  }
  if (status !== null && status >= 500) {
    return 'The server had a problem. Your sales are saved here and will upload shortly.';
  }

  return describeError(err, 'Could not upload to the server. Your sales are saved here.');
}
